// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import { ResponseMessageSchema, StatusSchema } from "./utilitySchemas";

export const RequestActivityLogTypeEnum = z.enum([
  "PAYMENT_REMINDERS_CANCELED",
  "REQUEST_CANCELLED",
  "REQUEST_REASSIGNED",
  "REQUEST_CONVERTED_TO_INTERNAL",
  "REQUEST_SUBMITTED",
  "REQUEST_CREATED",
  "REQUEST_STATUS_CHANGED",
  "AUTO_IA_SUBMITTED",
  "EXTENDED_IA_SUBMITTED",
  "FILES_UPLOADED",
  "SYNC_TO_NINOX",
  "SYNC_FILES_TO_NINOX",
  "SYNC_FROM_NINOX",
  "INITIAL_ASSESSMENT_UPLOADED",
  "INITIAL_ASSESSMENT_SENT",
  "APPRAISAL_UPLOADED",
  "APPRAISAL_SENT",
  "PAYMENT_LINK_SENT",
  "PAYMENT_LINK_CHANGED",
  "PAYMENT_STATUS_CHANGED",
  "EXTRA_ADDED",
  "EXTRA_REMOVED",
  "VIEWING_CLEARED",
  "VIEWING_MARKED_TO_BE_SCHEDULED",
  "VIEWING_SCHEDULED",
  "VIEWING_COMPLETED",
  "VIEWING_OFFER_ACCEPTED",
  "VIEWING_OFFER_DECLINED",
  "VIEWING_DEADLINE_CHANGED",
  "VIEWING_DISPATCH_STATUS_CHANGED",
  "VIEWING_INSPECTOR_NOTES_CHANGED",
  "VIEWING_INSPECTOR_ASSIGNED",
  "TAX_PLAUSIBILITY_CHECK_CHANGED",
  "ORDER_CONFIRMED_WITH_TERMS",
  "REQUESTER_EMAIL_CHANGED",
  "REQUEST_OWNER_CHANGED",
  "MANUAL_INVOICE_REFERENCE_CHANGED",
  "REQUEST_FLAG_CHANGED",
  "EXTERNAL_PPA_SUMMARY_UPDATED",
]);

export type RequestActivityLogTypes = z.infer<
  typeof RequestActivityLogTypeEnum
>;

export const RequestActivityLogOrderSchema = z.enum(["asc", "desc"]);

const RequestActivityLogActionFilterSchema = z.preprocess((value) => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) =>
      typeof entry === "string"
        ? entry
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        : [],
    );
  }

  return value;
}, z.array(RequestActivityLogTypeEnum).min(1).optional());

export const RequestActivityLogQuerySchema = z
  .object({
    limit: z.coerce.number().int().positive().max(200).default(100),
    offset: z.coerce.number().int().min(0).default(0),
    order: RequestActivityLogOrderSchema.default("desc"),
    filter: z
      .object({
        requestActivity: z
          .object({
            action: RequestActivityLogActionFilterSchema,
            isExternal: z
              .union([z.boolean(), z.literal("true"), z.literal("false")])
              .optional()
              .transform((v) =>
                v === undefined ? undefined : v === true || v === "true",
              ),
            dateRange: z
              .object({
                from: z.coerce.date().optional(),
                to: z.coerce.date().optional(),
              })
              .optional(),
          })
          .optional(),
      })
      .optional(),
  })
  .refine(
    (data) => {
      const from = data.filter?.requestActivity?.dateRange?.from;
      const to = data.filter?.requestActivity?.dateRange?.to;
      if (from && to) {
        return from <= to;
      }
      return true;
    },
    {
      message: "The 'from' date must be earlier than the 'to' date.",
      path: ["filter", "requestActivity", "dateRange", "from"],
    },
  );

export type RequestActivityLogQuery = z.infer<
  typeof RequestActivityLogQuerySchema
>;

export const RequestActivityLogItemSchema = z.object({
  id: z.string(),
  action: RequestActivityLogTypeEnum,
  actionLabel: z.string(),
  actorDisplayName: z.string(),
  createdAt: z.string().datetime(),
  detailText: z.string().nullable(),
  beforeValue: z.string().nullable(),
  afterValue: z.string().nullable(),
  isExternal: z.boolean(),
});

export type RequestActivityLogItem = z.infer<
  typeof RequestActivityLogItemSchema
>;

export const RequestActivityLogListResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema.optional(),
  data: z.object({
    listing: z.array(RequestActivityLogItemSchema),
  }),
});

export type RequestActivityLogListResponse = z.infer<
  typeof RequestActivityLogListResponseSchema
>;
